// src/ui/App.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Login from './components/Login';
import Register from './components/Register';
import { resolveInitialDashboardAppFromLocation } from './components/dashboard/dashboardRouteResolver';
import ReservationPage from './components/public/ReservationPage';
import ManageReservationPage from './components/public/ManageReservationPage';
import { PublicRouteDiagnosticsBoundary } from './diagnostics/PublicRouteDiagnosticsBoundary';

import {
  User,
  Request,
  Shift,
  Todo,
  Unit,
  RolePermissions,
  Permissions,
  demoUser,
  demoData,
  TimeEntry,
  Feedback,
  Poll,
} from '../core/models/data';

import { auth, db, functions } from '../core/firebase/config';
import { authErrorDetails, logAuthDiagnostic, logAuthLocalStorageSummary } from '../core/auth/authDiagnostics';
import { onAuthStateChanged, onIdTokenChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  documentId,
  type DocumentData,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';

import LoadingSpinner from '../../components/LoadingSpinner';
import { UnitProvider, useUnitContext } from './context/UnitContext';
import UnitSelectionGate from './components/UnitSelectionGate';

import ThemeManager from '../core/theme/ThemeManager';
import { ThemeMode, ThemeBases } from '../core/theme/types';
import { loadBases, loadMode, saveBases, saveMode } from '../core/theme/storage';
import { warmBackend } from '../core/services/backendWarmupService';
import { deleteSchedulerSnapshotsForUser } from '../core/services/schedulerSnapshotStore';
import { listUsersViaApi } from '../core/services/userApiService';
import { getGlobalPermissions as getGlobalPermissionsViaApi } from '../core/services/permissionsApiService';
import { getUnitPermissions as getUnitPermissionsViaApi } from '../core/services/unitPermissionsApiService';
import { listDashboardRequests, listDashboardShifts, type DashboardShiftReadOptions } from '../core/services/dashboardReadApiService';
import { getCurrentUserIdentityIds } from './utils/currentUserIdentity';
import { startModalBackdropScrollLockObserver } from './utils/bodyScrollLock';
import { resolveLeaveRequestPermissionScope } from './permissions/leaveRequestPermissions';
import { isAbortLikeError } from './components/apps/scheduler/utils/schedulerLoadErrors';
import { SchedulerRefreshController, type SchedulerRefreshReason } from './components/apps/scheduler/state/schedulerRefreshController';
import { schedulerBootstrapProfiler } from './components/apps/scheduler/state/schedulerBootstrapProfiler';
import { buildSchedulerRefreshScopeKey, createInitialSchedulerAppOpenBootstrapState, resolveSchedulerAppOpenIdempotency, resolveSchedulerAppOpenLifecycleCancelled, resolveSchedulerBootstrapRefreshDedupe, type SchedulerAppOpenBootstrapState, type SchedulerBootstrapRefreshDedupeState } from './components/apps/scheduler/state/schedulerBootstrapRefreshDedupe';
import { isSoftSchedulerRefreshReason, resolveSchedulerRefreshResponseDecision } from './components/apps/scheduler/state/schedulerRefreshResponseGuard';

const loadDashboard = () => import('./components/Dashboard');
const Dashboard = React.lazy(loadDashboard);

const PERMISSION_REFRESH_EVENT = 'mintleaf:permissions-refresh';

type AppState = 'login' | 'register' | 'dashboard' | 'loading' | 'public' | 'auth-profile-error';
type LoginMessage = { type: 'success' | 'error'; text: string };

type SchedulerShiftWindow = { fromDateKey: string; toDateKey: string };
type SchedulerRefreshOptions = { window?: SchedulerShiftWindow };
type SchedulerPendingRefreshWindow = { id: number; reason: SchedulerRefreshReason; window: SchedulerShiftWindow };

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const parseDateKey = (dateKey: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
  const parsed = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildBufferedSchedulerShiftWindow = (visibleWindow: SchedulerShiftWindow): SchedulerShiftWindow => {
  const fromDate = parseDateKey(visibleWindow.fromDateKey);
  const toDate = parseDateKey(visibleWindow.toDateKey);
  if (!fromDate || !toDate) return visibleWindow;
  return {
    fromDateKey: toDateKey(addDays(fromDate, -7)),
    toDateKey: toDateKey(addDays(toDate, 14)),
  };
};

const mergeShiftsById = (base: Shift[], incoming: Shift[]): Shift[] => {
  const byId = new Map<string, Shift>();
  base.forEach(shift => byId.set(shift.id, shift));
  incoming.forEach(shift => byId.set(shift.id, shift));
  return Array.from(byId.values());
};

const windowContains = (outer: SchedulerShiftWindow, inner: SchedulerShiftWindow): boolean =>
  outer.fromDateKey <= inner.fromDateKey && outer.toDateKey >= inner.toDateKey;

const isWindowHydrated = (ranges: SchedulerShiftWindow[], requested: SchedulerShiftWindow): boolean =>
  ranges.some(range => windowContains(range, requested));

type AuthBootstrapStatus =
  | 'idle'
  | 'auth-loading'
  | 'profile-loading'
  | 'profile-ready'
  | 'profile-error'
  | 'signed-out';
type PublicPage =
  | { type: 'reserve'; unitId: string }
  | { type: 'manage'; unitId: string; reservationId: string; manageToken: string }
  | { type: 'error'; message: string };

type ResolvedUserBootstrapPayload = {
  id: string;
  name?: string;
  lastName?: string;
  firstName?: string;
  fullName?: string;
  email?: string;
  role?: User['role'];
  unitIds?: string[];
  position?: string;
  dashboardConfig?: User['dashboardConfig'];
  authUid?: string;
  accessProfileVersion?: number;
};

type ResolveUserDocByAuthUidResponse =
  | { ok: true; userId: string; user?: ResolvedUserBootstrapPayload }
  | { ok: false; reason: 'not-found' };

type RulesAccessProfile = {
  authUid?: string;
  appUserId?: string;
  canonicalUserId?: string;
  role?: User['role'];
  unitIds?: string[];
  sourceUserDocId?: string;
  disabled?: boolean;
  suspended?: boolean;
  accessProfileVersion?: number;
};

const isPermissionDeniedError = (err: any): boolean => {
  const rawCode = typeof err?.code === 'string' ? err.code : '';
  const normalizedCode = rawCode.replace(/^functions\//, '');
  return normalizedCode === 'permission-denied';
};


type SessionStorageKeyCategory =
  | 'firebase-auth'
  | 'mintleaf-api-token'
  | 'mintleaf-unit-restore'
  | 'mintleaf-selected-units'
  | 'mintleaf-auth-diagnostics'
  | 'other';

const categorizeSessionStorageKey = (key: string | null): SessionStorageKeyCategory => {
  if (!key) return 'other';
  if (key.includes('firebase') || key.includes('authUser')) return 'firebase-auth';
  if (key === 'mintleaf_api_token') return 'mintleaf-api-token';
  if (key === UNIT_RESTORE_KEY || key === WORKSPACE_RESTORE_KEY) return 'mintleaf-unit-restore';
  if (key.startsWith('mintleaf_selected_units_')) return 'mintleaf-selected-units';
  if (key === 'mintleaf.authDiagnostics') return 'mintleaf-auth-diagnostics';
  return 'other';
};

const isAuthSessionStorageCategory = (category: SessionStorageKeyCategory): boolean =>
  category === 'firebase-auth' || category === 'mintleaf-api-token';

const removeLegacyMintleafApiToken = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('mintleaf_api_token');
  } catch (error) {
    logAuthDiagnostic('session-storage:changed', {
      keyCategory: 'mintleaf-api-token',
      storageArea: 'localStorage',
      likelyAffectsAuth: true,
      operation: 'cleanup-failed',
      error: authErrorDetails(error),
    });
  }
};

const WORKSPACE_RESTORE_KEY = 'mintleaf.workspace.restore';
const WORKSPACE_RESTORE_TTL_MS = 4 * 60 * 60 * 1000;
const UNIT_RESTORE_KEY = 'mintleaf.unit.restore';
const UNIT_RESTORE_TTL_MS = 24 * 60 * 60 * 1000;
const isDev = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

type WorkspaceRestorePayload = {
  userId?: string;
  activeUnitIds?: string[];
  activeAppId?: string;
  selectedDivisionId?: string | null;
  savedAt?: number;
};

type UnitRestorePayload = {
  userId?: string;
  activeUnitIds?: string[];
  savedAt?: number;
};

const readUnitRestorePayload = (): UnitRestorePayload | null => {
  const raw = window.localStorage.getItem(UNIT_RESTORE_KEY);
  if (!raw) return null;
  return JSON.parse(raw) as UnitRestorePayload;
};

const isUnitRestoreExpired = (savedAt?: number): boolean =>
  !savedAt || Date.now() - savedAt > UNIT_RESTORE_TTL_MS;

const validateWorkspaceUnits = (params: {
  payloadUnitIds?: string[];
  userUnitIds: string[];
  allUnits: Unit[];
}): string[] => {
  const userUnitSet = new Set(params.userUnitIds);
  const existingUnitSet = new Set(params.allUnits.map(unit => unit.id));
  return (Array.isArray(params.payloadUnitIds) ? params.payloadUnitIds : []).filter(
    unitId => typeof unitId === 'string' && userUnitSet.has(unitId) && existingUnitSet.has(unitId)
  );
};

const DashboardWithUnitGate: React.FC<{
  currentUser: User | null;
  allUnits: Unit[];
  children: React.ReactNode;
}> = ({ currentUser, allUnits, children }) => {
  const { selectedUnits, setSelectedUnits } = useUnitContext();
  const [isRestoreCheckDone, setIsRestoreCheckDone] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setIsRestoreCheckDone(true);
      return;
    }
    const userUnitIds = Array.isArray(currentUser.unitIds) ? currentUser.unitIds : [];
    const hasAssignedUnits = userUnitIds.length > 0;
    const unitsReady = allUnits.length > 0 || !hasAssignedUnits;
    if (!unitsReady) {
      if (isDev) {
        console.debug('[workspace-restore] waiting for units', {
          userId: currentUser.id,
          userUnitCount: userUnitIds.length,
          allUnitsCount: allUnits.length,
        });
      }
      return;
    }

    try {
      const payload = readUnitRestorePayload();
      if (!payload) {
        setIsRestoreCheckDone(true);
        return;
      }
      if (isDev) {
        console.debug('[unit-restore] payload found', { userId: currentUser.id, payload });
      }
      if (payload.userId !== currentUser.id) {
        if (isDev) console.debug('[unit-restore] invalid', { reason: 'user-mismatch', payloadUserId: payload.userId, currentUserId: currentUser.id });
        window.localStorage.removeItem(UNIT_RESTORE_KEY);
        setIsRestoreCheckDone(true);
        return;
      }
      if (isUnitRestoreExpired(payload.savedAt)) {
        if (isDev) console.debug('[unit-restore] expired', { userId: currentUser.id });
        window.localStorage.removeItem(UNIT_RESTORE_KEY);
        setIsRestoreCheckDone(true);
        return;
      }
      const validUnits = validateWorkspaceUnits({
        payloadUnitIds: payload.activeUnitIds,
        userUnitIds,
        allUnits,
      });
      if (validUnits.length > 0) {
        setSelectedUnits(validUnits);
        if (isDev) console.debug('[unit-restore] restored units', { userId: currentUser.id, units: validUnits });
      } else {
        if (isDev) {
          console.debug('[unit-restore] invalid', {
            userId: currentUser.id,
            payloadUnits: payload.activeUnitIds || [],
          });
        }
        window.localStorage.removeItem(UNIT_RESTORE_KEY);
      }
    } catch {
      if (isDev) console.debug('[unit-restore] invalid', { reason: 'invalid-json' });
      try {
        window.localStorage.removeItem(UNIT_RESTORE_KEY);
      } catch {
        // no-op
      }
    } finally {
      setIsRestoreCheckDone(true);
    }
  }, [allUnits, currentUser, setSelectedUnits]);

  if (!isRestoreCheckDone) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <UnitSelectionGate
      currentUser={currentUser}
      allUnits={allUnits}
      selectedUnits={selectedUnits}
      setSelectedUnits={setSelectedUnits}
    >
      {children}
    </UnitSelectionGate>
  );
};

const ThemeManagerBridge: React.FC<{
  allUnits: Unit[];
  bases: ThemeBases;
  previewBases?: ThemeBases | null;
  themeMode: ThemeMode;
  useBrandTheme: boolean;
}> = ({ allUnits, bases, previewBases, themeMode, useBrandTheme }) => {
  const { selectedUnits } = useUnitContext();

  const activeUnit = selectedUnits.length
    ? allUnits.find(u => u.id === selectedUnits[0]) || null
    : null;

  const resolvedBases = previewBases || bases;

  return (
    <ThemeManager
      activeUnit={activeUnit}
      themeMode={themeMode}
      useBrandTheme={useBrandTheme}
      adminConfig={resolvedBases}
    />
  );
};


const CLAIM_BOOTSTRAP_GUARD_KEY = 'mintleaf_claim_existing_in_progress';

const isClaimBootstrapGuardActive = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage.getItem(CLAIM_BOOTSTRAP_GUARD_KEY));
  } catch {
    return false;
  }
};

const clearClaimBootstrapGuard = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(CLAIM_BOOTSTRAP_GUARD_KEY);
  } catch {
    // no-op
  }
};

const normalizeUserUnitIds = (data: any): string[] => {
  const u1 = Array.isArray(data?.unitIds) ? data.unitIds : [];
  const u2 = Array.isArray(data?.unitIDs) ? data.unitIDs : [];
  const uLegacy = Array.isArray(data?.unitID) ? data.unitID : [];
  const u3 = typeof data?.unitId === 'string' && data.unitId ? [data.unitId] : [];
  return Array.from(new Set([...u1, ...u2, ...uLegacy, ...u3]
    .filter((unitId): unitId is string => typeof unitId === 'string' && unitId.trim().length > 0)
    .map(unitId => unitId.trim())));
};

const intersectUnitIds = (candidateUnitIds: string[], allowedUnitIds: string[]): string[] => {
  const allowed = new Set(
    allowedUnitIds
      .map(unitId => (typeof unitId === 'string' ? unitId.trim() : ''))
      .filter(Boolean)
  );
  return Array.from(new Set(
    candidateUnitIds
      .map(unitId => (typeof unitId === 'string' ? unitId.trim() : ''))
      .filter(unitId => unitId.length > 0 && allowed.has(unitId))
  ));
};

const isCompleteUserProfile = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false;

  const fullName = typeof data.fullName === 'string' ? data.fullName.trim() : '';
  const name = typeof data.name === 'string' ? data.name.trim() : '';
  const firstName = typeof data.firstName === 'string' ? data.firstName.trim() : '';
  const lastName = typeof data.lastName === 'string' ? data.lastName.trim() : '';
  const hasDisplayIdentity = Boolean(fullName || name || firstName || lastName);

  const role = typeof data.role === 'string' ? data.role.trim() : '';
  const userType = typeof data.usertype === 'string' ? data.usertype.trim() : '';
  const hasMeaningfulRole = Boolean(role || userType);

  return hasDisplayIdentity && hasMeaningfulRole;
};

type ProfileBootstrapSource = 'auth-state' | 'manual-retry';

type ProfileBootstrapResult =
  | { ok: true; user: User; resolvedUserDocId: string }
  | { ok: false; reason: string; safeMessage: string; recoverable: boolean };

type ProfileBootstrapOptions = {
  source: ProfileBootstrapSource;
  shouldKeepRegisterOnResolveError?: () => boolean;
};

const mapResolvedUserToAppUser = (
  resolvedData: any,
  resolvedUserDocId: string,
  firebaseUser: FirebaseUser
): User => {
  const unitIds = normalizeUserUnitIds(resolvedData);
  const lastName = resolvedData?.lastName || '';
  const firstName = resolvedData?.firstName || '';

  return {
    id: resolvedUserDocId,
    name: resolvedData?.name || firebaseUser.email!,
    lastName,
    firstName,
    fullName: resolvedData?.fullName || `${lastName} ${firstName}`.trim(),
    email: resolvedData?.email || firebaseUser.email!,
    role: resolvedData?.role || 'User',
    unitIds,
    position: resolvedData?.position,
    dashboardConfig: resolvedData?.dashboardConfig,
    authUid: typeof resolvedData?.authUid === 'string' && resolvedData.authUid ? resolvedData.authUid : firebaseUser.uid,
    accessProfileVersion: typeof resolvedData?.accessProfileVersion === 'number' ? resolvedData.accessProfileVersion : 0,
  };
};

const runProfileBootstrap = async (
  firebaseUser: FirebaseUser,
  options: ProfileBootstrapOptions
): Promise<ProfileBootstrapResult> => {
  logAuthDiagnostic('profile-bootstrap:start', { uid: firebaseUser.uid, source: options.source });

  try {
    const authUidUserDocRef = doc(db, 'users', firebaseUser.uid);
    const resolveBoundUserDoc = httpsCallable<Record<string, never>, ResolveUserDocByAuthUidResponse>(
      functions,
      'resolveUserDocByAuthUid'
    );

    let resolvedUserDocId = firebaseUser.uid;
    let resolvedData: any | null = null;
    let directUserDataForDebug: any | null = null;

    try {
      logAuthDiagnostic('profile-read:start', { uid: firebaseUser.uid, source: 'users/authUid' });
      const userDoc = await getDoc(authUidUserDocRef);
      directUserDataForDebug = userDoc.exists() ? userDoc.data() : null;
      resolvedData = directUserDataForDebug;
      logAuthDiagnostic('profile-read:success', {
        uid: firebaseUser.uid,
        source: 'users/authUid',
        exists: userDoc.exists(),
      });
    } catch (directReadError: any) {
      logAuthDiagnostic('profile-read:error', {
        uid: firebaseUser.uid,
        source: 'users/authUid',
        error: authErrorDetails(directReadError),
      });
      if (isPermissionDeniedError(directReadError)) resolvedData = null;
      else throw directReadError;
    }

    const authUidDocHasUnitScope = normalizeUserUnitIds(resolvedData).length > 0;
    const authUidDocIsComplete = isCompleteUserProfile(resolvedData) && authUidDocHasUnitScope;
    if (!resolvedData || !authUidDocIsComplete) {
      try {
        const resolveResult = await resolveBoundUserDoc({});
        const resolvedPayload = resolveResult.data;
        if (resolvedPayload?.ok && resolvedPayload.userId) {
          resolvedUserDocId = resolvedPayload.userId;
          if (resolvedPayload.user) resolvedData = resolvedPayload.user;
          else console.warn('resolveUserDocByAuthUid returned userId without user payload');
        } else if (resolvedPayload?.reason === 'not-found' && !resolvedData) {
          resolvedData = null;
        }
      } catch (resolveError: any) {
        const rawCode = typeof resolveError?.code === 'string' ? resolveError.code : '';
        const normalizedCode = rawCode.replace(/^functions\//, '');
        console.error('resolveUserDocByAuthUid failed', authErrorDetails(resolveError));
        logAuthDiagnostic('profile-read:error', {
          uid: firebaseUser.uid,
          reason: `resolve-user-doc-${normalizedCode || 'error'}`,
          error: authErrorDetails(resolveError),
        });

        if (normalizedCode === 'not-found') {
          resolvedData = null;
        } else if (normalizedCode === 'failed-precondition') {
          return { ok: false, reason: 'resolve-user-doc-failed-precondition', safeMessage: 'Profil betöltési hiba: failed-precondition.', recoverable: true };
        } else if (normalizedCode === 'permission-denied' || normalizedCode === 'internal') {
          if (options.shouldKeepRegisterOnResolveError?.()) {
            return { ok: false, reason: 'claim-bootstrap-guard-active', safeMessage: 'Profil betöltés késleltetve.', recoverable: true };
          }
          return { ok: false, reason: `resolve-user-doc-${normalizedCode || 'error'}`, safeMessage: `Profil betöltési hiba: ${normalizedCode || 'resolve-error'}.`, recoverable: true };
        } else {
          throw resolveError;
        }
      }
    }

    if (resolvedUserDocId !== firebaseUser.uid || !resolvedData || !authUidDocIsComplete) {
      try {
        logAuthDiagnostic('profile-read:start', { uid: firebaseUser.uid, userDocId: resolvedUserDocId, source: 'users/resolved' });
        const resolvedUserDoc = await getDoc(doc(db, 'users', resolvedUserDocId));
        resolvedData = resolvedUserDoc.exists() ? resolvedUserDoc.data() : null;
        logAuthDiagnostic('profile-read:success', {
          uid: firebaseUser.uid,
          userDocId: resolvedUserDocId,
          source: 'users/resolved',
          exists: resolvedUserDoc.exists(),
        });
      } catch (resolvedReadError) {
        logAuthDiagnostic('profile-read:error', {
          uid: firebaseUser.uid,
          userDocId: resolvedUserDocId,
          source: 'users/resolved',
          error: authErrorDetails(resolvedReadError),
        });
        throw resolvedReadError;
      }
    }

    if (!resolvedData) {
      logAuthDiagnostic('profile-read:error', { uid: firebaseUser.uid, reason: 'profile-not-found' });
      const result = { ok: false, reason: 'profile-not-found', safeMessage: 'Profil betöltési hiba: a felhasználói profil nem található.', recoverable: true } as const;
      logAuthDiagnostic('profile-bootstrap:error', { uid: firebaseUser.uid, source: options.source, reason: result.reason });
      return result;
    }

    const user = mapResolvedUserToAppUser(resolvedData, resolvedUserDocId, firebaseUser);
    const hasName = Boolean(user.name || user.fullName || user.firstName || user.lastName);
    const hasRole = Boolean(user.role);
    if (!hasName || !hasRole) {
      logAuthDiagnostic('profile-bootstrap:error', { uid: firebaseUser.uid, reason: 'profile-incomplete', hasName, hasRole });
      return { ok: false, reason: 'profile-incomplete', safeMessage: 'Profil betöltési hiba: hiányos felhasználói profil.', recoverable: true };
    }

    if (import.meta.env.DEV) {
      console.debug('resolved-user-debug', {
        authUid: firebaseUser.uid,
        directUserDocId: firebaseUser.uid,
        linkedUserId: typeof directUserDataForDebug?.linkedUserId === 'string' ? directUserDataForDebug.linkedUserId : null,
        canonicalUserId: typeof directUserDataForDebug?.canonicalUserId === 'string' ? directUserDataForDebug.canonicalUserId : null,
        appUserId: typeof directUserDataForDebug?.appUserId === 'string' ? directUserDataForDebug.appUserId : null,
        resolvedUserDocId,
        resolvedUserUnitIds: user.unitIds,
        resolvedUserRole: user.role,
      });
    }

    logAuthDiagnostic('profile-bootstrap:success', { uid: firebaseUser.uid, source: options.source, userDocId: resolvedUserDocId });
    return { ok: true, user, resolvedUserDocId };
  } catch (error) {
    console.error('Error fetching user data:', authErrorDetails(error));
    logAuthDiagnostic('profile-bootstrap:error', { uid: firebaseUser.uid, source: options.source, error: authErrorDetails(error) });
    return { ok: false, reason: 'profile-bootstrap-error', safeMessage: 'Profil betöltési hiba: az app nem tudta betölteni a felhasználói profilt.', recoverable: true };
  }
};

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>('loading');
  const [publicPage, setPublicPage] = useState<PublicPage | null>(null);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [accessProfile, setAccessProfile] = useState<RulesAccessProfile | null>(null);
  const [isAccessProfileLoaded, setIsAccessProfileLoaded] = useState(false);
  const [firebaseAuthUid, setFirebaseAuthUid] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [loginMessage, setLoginMessage] = useState<LoginMessage | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [authBootstrapError, setAuthBootstrapError] = useState<string | null>(null);
  const [authBootstrapStatus, setAuthBootstrapStatus] = useState<AuthBootstrapStatus>('idle');

  const [firestoreError, setFirestoreError] = useState<string | null>(null);

  // --- Data States ---
  const [requests, setRequests] = useState<Request[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [isShiftsLoading, setIsShiftsLoading] = useState(true);
  const [isShiftsRefreshing, setIsShiftsRefreshing] = useState(false);
  const [hasLoadedShiftsOnce, setHasLoadedShiftsOnce] = useState(false);
  const [loadedSchedulerShiftScopeKey, setLoadedSchedulerShiftScopeKey] = useState<string | null>(null);
  const [schedulerRefreshWarning, setSchedulerRefreshWarning] = useState<string | null>(null);
  const [dashboardTodayShiftStatus, setDashboardTodayShiftStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const schedulerRefreshControllerRef = useRef<SchedulerRefreshController | null>(null);
  const schedulerRefreshUnitIdsRef = useRef<string[]>([]);
  const schedulerRefreshSequenceRef = useRef(0);
  const latestStartedSchedulerRefreshSequenceRef = useRef(0);
  const shiftsRef = useRef<Shift[]>([]);
  const hasLoadedShiftsOnceRef = useRef(false);
  const loadedSchedulerShiftScopeKeyRef = useRef<string | null>(null);
  const schedulerBootstrapRefreshDedupeRef = useRef<SchedulerBootstrapRefreshDedupeState>({
    inFlightScopeKey: null,
    completedScopeKey: null,
    firstPaintCompleted: false,
  });
  const schedulerAppOpenBootstrapRef = useRef<SchedulerAppOpenBootstrapState>(createInitialSchedulerAppOpenBootstrapState());
  const schedulerHydratedShiftWindowsRef = useRef<SchedulerShiftWindow[]>([]);
  const schedulerVisibleShiftWindowRef = useRef<SchedulerShiftWindow | null>(null);
  const schedulerRefreshWindowRequestIdRef = useRef(0);
  const schedulerRefreshWindowBySequenceRef = useRef(new Map<number, SchedulerPendingRefreshWindow>());
  const dashboardTodayShiftBootstrapRef = useRef<{ scopeKey: string | null; inFlight: boolean; completed: boolean }>({
    scopeKey: null,
    inFlight: false,
    completed: false,
  });
  useEffect(() => {
    shiftsRef.current = shifts;
  }, [shifts]);

  useEffect(() => {
    loadedSchedulerShiftScopeKeyRef.current = loadedSchedulerShiftScopeKey;
  }, [loadedSchedulerShiftScopeKey]);

  const refreshSchedulerShifts = useCallback(async (reason: SchedulerRefreshReason = 'manual', options?: SchedulerRefreshOptions) => {
    const controller = schedulerRefreshControllerRef.current;
    if (!controller) return;

    const scopeKey = buildSchedulerRefreshScopeKey({
      unitIds: schedulerRefreshUnitIdsRef.current,
      userId: currentUserRef.current?.id || null,
    });
    const hasUsableLoadedScope =
      hasLoadedShiftsOnceRef.current &&
      loadedSchedulerShiftScopeKeyRef.current === scopeKey;
    const isSoftRefresh = isSoftSchedulerRefreshReason(reason);
    const isAppOpenRefresh = reason === 'app_open';
    const requestedWindow = options?.window;
    if (reason === 'week_change' && requestedWindow && isWindowHydrated(schedulerHydratedShiftWindowsRef.current, requestedWindow)) {
      schedulerBootstrapProfiler.recordRefresh({
        type: 'deduped',
        reason,
        mode: 'soft',
        detail: `shift-api:window-already-hydrated:from=${requestedWindow.fromDateKey}:to=${requestedWindow.toDateKey}`,
      });
      return;
    }
    if (isAppOpenRefresh && !requestedWindow && !hasUsableLoadedScope) {
      schedulerBootstrapProfiler.recordRefresh({
        type: 'deduped',
        reason,
        mode: 'hard',
        detail: 'shift-api:app-open-waiting-for-visible-window',
      });
      return;
    }

    if (isAppOpenRefresh) {
      const appOpenDecision = resolveSchedulerAppOpenIdempotency({
        scopeKey,
        state: schedulerAppOpenBootstrapRef.current,
      });
      if (appOpenDecision.action === 'dedupe') {
        schedulerBootstrapProfiler.recordRefresh({
          type: 'deduped',
          reason,
          mode: 'hard',
          detail: appOpenDecision.reason,
        });
        return;
      }
      schedulerAppOpenBootstrapRef.current = {
        scopeKey,
        inFlight: true,
        completed: false,
        firstPaintCompleted: schedulerAppOpenBootstrapRef.current.firstPaintCompleted,
      };
    }

    const dedupeDecision = resolveSchedulerBootstrapRefreshDedupe({
      reason,
      scopeKey,
      state: schedulerBootstrapRefreshDedupeRef.current,
    });

    if (dedupeDecision.action === 'dedupe') {
      schedulerBootstrapProfiler.recordRefresh({
        type: 'deduped',
        reason,
        mode: isSoftRefresh ? 'soft' : 'hard',
        detail: dedupeDecision.reason,
      });
      if (isAppOpenRefresh && schedulerAppOpenBootstrapRef.current.scopeKey === scopeKey) {
        schedulerAppOpenBootstrapRef.current.inFlight = false;
      }
      return;
    }

    const tracksBootstrapScope = !isSoftRefresh && !schedulerBootstrapRefreshDedupeRef.current.firstPaintCompleted;
    if (tracksBootstrapScope) {
      schedulerBootstrapRefreshDedupeRef.current.inFlightScopeKey = scopeKey;
    }

    const requestSequence = schedulerRefreshSequenceRef.current + 1;
    schedulerRefreshSequenceRef.current = requestSequence;
    if (requestedWindow) {
      const refreshWindowRequestId = schedulerRefreshWindowRequestIdRef.current + 1;
      schedulerRefreshWindowRequestIdRef.current = refreshWindowRequestId;
      schedulerRefreshWindowBySequenceRef.current.set(requestSequence, { id: refreshWindowRequestId, reason, window: requestedWindow });
    }

    try {
      await controller.requestRefresh(reason, { requestSequence });
      if (isAppOpenRefresh && schedulerAppOpenBootstrapRef.current.scopeKey === scopeKey) {
        schedulerAppOpenBootstrapRef.current.completed = true;
      }
      if (tracksBootstrapScope && schedulerBootstrapRefreshDedupeRef.current.inFlightScopeKey === scopeKey) {
        schedulerBootstrapRefreshDedupeRef.current.completedScopeKey = scopeKey;
      }
    } finally {
      if (isAppOpenRefresh && schedulerAppOpenBootstrapRef.current.scopeKey === scopeKey) {
        schedulerAppOpenBootstrapRef.current.inFlight = false;
      }
      if (tracksBootstrapScope && schedulerBootstrapRefreshDedupeRef.current.inFlightScopeKey === scopeKey) {
        schedulerBootstrapRefreshDedupeRef.current.inFlightScopeKey = null;
      }
      schedulerRefreshWindowBySequenceRef.current.delete(requestSequence);
    }
  }, []);

  const markSchedulerFirstPaintComplete = useCallback(() => {
    schedulerBootstrapRefreshDedupeRef.current.firstPaintCompleted = true;
    schedulerAppOpenBootstrapRef.current.firstPaintCompleted = true;
  }, []);

  const maybeStartSchedulerWindowedAppOpen = useCallback(() => {
    const controller = schedulerRefreshControllerRef.current;
    const visibleWindow = schedulerVisibleShiftWindowRef.current;
    const scopeKey = buildSchedulerRefreshScopeKey({
      unitIds: schedulerRefreshUnitIdsRef.current,
      userId: currentUserRef.current?.id || null,
    });
    const hasUsableLoadedScope =
      hasLoadedShiftsOnceRef.current &&
      loadedSchedulerShiftScopeKeyRef.current === scopeKey;
    if (!controller || !visibleWindow || hasUsableLoadedScope) return;
    void refreshSchedulerShifts('app_open', { window: visibleWindow });
  }, [refreshSchedulerShifts]);

  const handleSchedulerVisibleRangeChange = useCallback((visibleWindow: SchedulerShiftWindow) => {
    schedulerVisibleShiftWindowRef.current = buildBufferedSchedulerShiftWindow(visibleWindow);
    maybeStartSchedulerWindowedAppOpen();
  }, [maybeStartSchedulerWindowedAppOpen]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [adminTodos, setAdminTodos] = useState<Todo[]>([]);
  const [allUnits, setAllUnits] = useState<Unit[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<RolePermissions>({});
  const [unitPermissions, setUnitPermissions] = useState<Record<string, any>>({});
  const [permissionRefreshNonce, setPermissionRefreshNonce] = useState(0);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const appStateRef = useRef<AppState>(appState);
  const dashboardActiveAppRef = useRef<string>(resolveInitialDashboardAppFromLocation());
  const handleDashboardActiveAppChange = useCallback((activeApp: string) => {
    dashboardActiveAppRef.current = activeApp;
  }, []);
  const currentUserRef = useRef<User | null>(currentUser);
  const authBootstrapSequenceRef = useRef(0);
  const activeProfileBootstrapSequenceRef = useRef<number | null>(null);
  const firebaseAuthUidRef = useRef<string | null>(firebaseAuthUid);
  const hasSeenInitialAuthStateRef = useRef(false);
  const lastTokenStateUidRef = useRef<string | null>(null);

  appStateRef.current = appState;
  currentUserRef.current = currentUser;
  firebaseAuthUidRef.current = firebaseAuthUid;

  useEffect(() => {
    return startModalBackdropScrollLockObserver();
  }, []);

  // --- Theme States ---
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => loadMode());
  const [themeBases, setThemeBases] = useState<ThemeBases>(() => loadBases());
  const [previewBases, setPreviewBases] = useState<ThemeBases | null>(null);

  const [useBrandTheme, setUseBrandTheme] = useState<boolean>(() => {
    const saved = localStorage.getItem('mintleaf_use_brand');
    return saved !== null ? saved === 'true' : true;
  });
  const currentUserUnitKey = useMemo(
    () => (currentUser?.unitIds || [])
      .map(unitId => (typeof unitId === 'string' ? unitId.trim() : ''))
      .filter(Boolean)
      .sort()
      .join('|'),
    [currentUser?.unitIds]
  );
  const shouldShowAuthBootstrapDiagnostic = useMemo(() => {
    if (import.meta.env.DEV) return true;
    try {
      return localStorage.getItem('mintleaf.authDiagnostics') === '1';
    } catch {
      return false;
    }
  }, []);
  // ---------- URL routing + demo/register ----------
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    const isDemo = urlParams.get('demo') === 'true';
    const registerCode = urlParams.get('register');
    const isReservePage = pathname.startsWith('/reserve');
    const isManagePage = pathname.startsWith('/manage');

    if (isManagePage) {
      const manageToken = urlParams.get('token') || '';
      const reservationId = urlParams.get('reservationId') || '';
      const unitId = urlParams.get('unitId') || '';
      setPublicPage({ type: 'manage', unitId, reservationId, manageToken });
    } else if (isReservePage) {
      const unitId = urlParams.get('unit');
      if (unitId) setPublicPage({ type: 'reserve', unitId });
      else setPublicPage({ type: 'error', message: 'Nincs egység azonosító megadva a foglaláshoz.' });
    }

    // Demo mode: no Firestore
    if (isDemo) {
      setIsDemoMode(true);
      setAuthBootstrapStatus('profile-ready');
      setCurrentUser(demoUser);
      setRequests(demoData.requests);
      setShifts(demoData.shifts);
      setTodos(demoData.todos);
      setAdminTodos(demoData.adminTodos);
      setAllUnits(demoData.allUnits);
      setAllUsers(demoData.allUsers);
      setTimeEntries([]);
      setFeedbackList([]);
      setPolls([]);
      setPermissions({ 'Demo User': { canSubmitLeaveRequests: true, canManageTodos: true } });
      setAppState(isReservePage || isManagePage ? 'public' : 'dashboard');
      return;
    }

    // Register (invite) page
    if (registerCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setInviteCode(registerCode);
      setAppState('register');
      return;
    }

    // Default state until auth resolves
    setAuthBootstrapStatus('auth-loading');
    setAppState(isReservePage || isManagePage ? 'public' : 'loading');
  }, []);

  // ---------- Local theme persistence ----------
  useEffect(() => {
    saveMode(themeMode);
  }, [themeMode]);

  useEffect(() => {
    saveBases(themeBases);
  }, [themeBases]);

  useEffect(() => {
    setPreviewBases(null);
  }, [themeBases]);

  useEffect(() => {
    localStorage.setItem('mintleaf_use_brand', String(useBrandTheme));
  }, [useBrandTheme]);


  const clearSessionOwnedState = useCallback((options: {
    appState?: AppState;
    status?: AuthBootstrapStatus;
    clearLegacyApiToken?: boolean;
  } = {}) => {
    const signedOutUserId = currentUserRef.current?.id;
    if (signedOutUserId) {
      void deleteSchedulerSnapshotsForUser(signedOutUserId).catch(() => undefined);
    }
    authBootstrapSequenceRef.current += 1;
    activeProfileBootstrapSequenceRef.current = null;
    setCurrentUser(null);
    setAccessProfile(null);
    setIsAccessProfileLoaded(false);
    setFirebaseAuthUid(null);
    firebaseAuthUidRef.current = null;
    setLoadedSchedulerShiftScopeKey(null);
    loadedSchedulerShiftScopeKeyRef.current = null;
    setHasLoadedShiftsOnce(false);
    hasLoadedShiftsOnceRef.current = false;
    setAuthBootstrapError(null);
    setAuthBootstrapStatus(options.status || 'signed-out');
    if (options.clearLegacyApiToken) {
      removeLegacyMintleafApiToken();
    }
    if (options.appState) {
      setAppState(options.appState);
    }
  }, []);

  const getSignedOutAppState = useCallback((): AppState => {
    if (appStateRef.current === 'register' || Boolean(inviteCode)) return 'register';
    const pathname = window.location.pathname;
    return pathname.startsWith('/reserve') || pathname.startsWith('/manage') ? 'public' : 'login';
  }, [inviteCode]);

  const applyProfileBootstrapResult = useCallback((params: {
    sequence: number;
    firebaseUser: FirebaseUser;
    result: ProfileBootstrapResult;
    previousFirebaseAuthUid: string | null;
    isReservePage: boolean;
    isManagePage: boolean;
  }) => {
    if (authBootstrapSequenceRef.current !== params.sequence) {
      logAuthDiagnostic('profile-bootstrap:stale', {
        uid: params.firebaseUser.uid,
        sequence: params.sequence,
        activeSequence: authBootstrapSequenceRef.current,
      });
      return;
    }

    if (!params.result.ok) {
      if (params.result.reason === 'claim-bootstrap-guard-active') {
        setAppState('register');
        return;
      }
      setAuthBootstrapError(params.result.safeMessage);
      setCurrentUser(null);
      setAccessProfile(null);
      setIsAccessProfileLoaded(false);
      setAuthBootstrapStatus('profile-error');
      setAppState('auth-profile-error');
      return;
    }

    const activeUser = currentUserRef.current;
    const isSameBootstrapIdentity =
      params.previousFirebaseAuthUid === params.firebaseUser.uid && activeUser?.id === params.result.user.id;
    setCurrentUser(params.result.user);
    if (!isSameBootstrapIdentity) {
      setAccessProfile(null);
      setIsAccessProfileLoaded(false);
    } else if (import.meta.env.DEV) {
      console.debug('[auth-bootstrap] same firebase user and resolved user; preserving access profile state', {
        firebaseAuthUid: params.firebaseUser.uid,
        resolvedUserDocId: params.result.resolvedUserDocId,
        isAccessProfileLoaded,
        hasAccessProfile: Boolean(accessProfile),
      });
    }
    setAuthBootstrapStatus('profile-ready');
    setAppState(params.isReservePage || params.isManagePage ? 'public' : 'dashboard');
  }, [accessProfile, isAccessProfileLoaded]);

  const startProfileBootstrap = useCallback(async (
    firebaseUser: FirebaseUser,
    source: ProfileBootstrapSource,
    previousFirebaseAuthUid: string | null,
    isReservePage: boolean,
    isManagePage: boolean
  ) => {
    const activeProfileBootstrapSequence = activeProfileBootstrapSequenceRef.current;
    if (source === 'manual-retry' && activeProfileBootstrapSequence !== null) {
      logAuthDiagnostic('profile-bootstrap:skipped', {
        uid: firebaseUser.uid,
        source,
        reason: 'already-running',
        activeSequence: activeProfileBootstrapSequence,
      });
      return;
    }

    const sequence = authBootstrapSequenceRef.current + 1;
    authBootstrapSequenceRef.current = sequence;
    activeProfileBootstrapSequenceRef.current = sequence;

    setAuthBootstrapStatus('profile-loading');
    setAuthBootstrapError(null);

    try {
      const result = await runProfileBootstrap(firebaseUser, {
        source,
        shouldKeepRegisterOnResolveError: () =>
          isClaimBootstrapGuardActive() && (appStateRef.current === 'register' || Boolean(inviteCode)),
      });
      applyProfileBootstrapResult({
        sequence,
        firebaseUser,
        result,
        previousFirebaseAuthUid,
        isReservePage,
        isManagePage,
      });
    } finally {
      if (activeProfileBootstrapSequenceRef.current === sequence) {
        activeProfileBootstrapSequenceRef.current = null;
      }
    }
  }, [applyProfileBootstrapResult, inviteCode]);

  // ---------- Auth / user bootstrap ----------
  useEffect(() => {
    if (isDemoMode) return;

    const pathname = window.location.pathname;
    const isReservePage = pathname.startsWith('/reserve');
    const isManagePage = pathname.startsWith('/manage');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      setFirestoreError(null);
      setAuthBootstrapError(null);

      if (!firebaseUser) {
        logAuthDiagnostic('auth-state:null');
        hasSeenInitialAuthStateRef.current = true;
        const registerCodeFromUrl = new URLSearchParams(window.location.search).get('register');
        const shouldStayOnRegister =
          appStateRef.current === 'register' || Boolean(inviteCode) || Boolean(registerCodeFromUrl);

        if (shouldStayOnRegister) {
          console.log('keeping register state while unauthenticated invite flow is active');
          clearSessionOwnedState({ appState: 'register', clearLegacyApiToken: true });
          return;
        }

        clearClaimBootstrapGuard();
        clearSessionOwnedState({
          appState: isReservePage || isManagePage ? 'public' : 'login',
          clearLegacyApiToken: true,
        });
        return;
      }

      void warmBackend({ timeoutMs: 3500 });
      void loadDashboard();

      const isInitialAuthState = !hasSeenInitialAuthStateRef.current;
      hasSeenInitialAuthStateRef.current = true;
      logAuthDiagnostic('auth-state:user', { uid: firebaseUser.uid });
      if (isInitialAuthState) {
        logAuthDiagnostic('session-restore:detected', {
          uid: firebaseUser.uid,
          source: 'auth-state',
          profileBootstrapSource: 'auth-state',
          hasCurrentUser: Boolean(auth.currentUser),
        });
      }
      logAuthLocalStorageSummary();
      const previousFirebaseAuthUid = firebaseAuthUidRef.current;
      setFirebaseAuthUid(firebaseUser.uid);
      firebaseAuthUidRef.current = firebaseUser.uid;

      const claimGuardActive = isClaimBootstrapGuardActive();
      const isRegisterFlowActive = appStateRef.current === 'register' || Boolean(inviteCode);
      if (claimGuardActive && isRegisterFlowActive) {
        authBootstrapSequenceRef.current += 1;
        activeProfileBootstrapSequenceRef.current = null;
        logAuthDiagnostic('profile-bootstrap:skipped', { uid: firebaseUser.uid, source: 'auth-state', reason: 'claim-bootstrap-guard-active' });
        console.log('claim_existing bootstrap guard active, delaying auth bootstrap');
        setAppState('register');
        return;
      }

      await startProfileBootstrap(firebaseUser, 'auth-state', previousFirebaseAuthUid, isReservePage, isManagePage);
    });

    return () => {
      authBootstrapSequenceRef.current += 1;
      activeProfileBootstrapSequenceRef.current = null;
      unsubscribe();
    };
  }, [clearSessionOwnedState, isDemoMode, inviteCode, startProfileBootstrap]);

  // ---------- Token/session lifecycle observers ----------
  useEffect(() => {
    if (isDemoMode) return;

    const unsubscribe = onIdTokenChanged(
      auth,
      firebaseUser => {
        if (!firebaseUser) {
          lastTokenStateUidRef.current = null;
          authBootstrapSequenceRef.current += 1;
          activeProfileBootstrapSequenceRef.current = null;
          logAuthDiagnostic('token-state:null', {
            hasCurrentUser: Boolean(auth.currentUser),
          });
          return;
        }

        const previousTokenUid = lastTokenStateUidRef.current;
        const event = previousTokenUid === firebaseUser.uid ? 'token-state:refresh' : 'token-state:user';
        lastTokenStateUidRef.current = firebaseUser.uid;
        logAuthDiagnostic(event, {
          uid: firebaseUser.uid,
          hasCurrentUser: true,
        });
      },
      error => {
        logAuthDiagnostic('token-state:error', {
          hasCurrentUser: Boolean(auth.currentUser),
          error: authErrorDetails(error),
        });
      }
    );

    return () => unsubscribe();
  }, [isDemoMode]);

  useEffect(() => {
    if (isDemoMode || typeof window === 'undefined') return;

    const handleStorageChange = (event: StorageEvent) => {
      const keyCategory = categorizeSessionStorageKey(event.key);
      const likelyAffectsAuth = isAuthSessionStorageCategory(keyCategory);
      const storageArea = event.storageArea === window.sessionStorage ? 'sessionStorage' : 'localStorage';
      logAuthDiagnostic('session-storage:changed', {
        keyCategory,
        storageArea,
        likelyAffectsAuth,
        removed: event.newValue === null,
      });

      if (likelyAffectsAuth && event.newValue === null) {
        logAuthDiagnostic('session-cross-tab:signout-detected', {
          keyCategory,
          storageArea,
          hasCurrentUser: Boolean(auth.currentUser),
        });
        clearSessionOwnedState({
          appState: getSignedOutAppState(),
          clearLegacyApiToken: keyCategory !== 'mintleaf-api-token',
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearSessionOwnedState, getSignedOutAppState, isDemoMode]);

  useEffect(() => {
    if (isDemoMode) {
      setAccessProfile(null);
      setIsAccessProfileLoaded(true);
      return;
    }
    if (!currentUser) {
      setAccessProfile(null);
      setIsAccessProfileLoaded(false);
      return;
    }
    if (!firebaseAuthUid) {
      console.warn('[access-profile.guard] missing Firebase Auth UID; using current user fallback for P0 only', {
        currentUserAuthUid: currentUser.authUid || null,
        currentUserId: currentUser.id,
      });
      setAccessProfile(null);
      setIsAccessProfileLoaded(true);
      return;
    }

    let cancelled = false;
    setIsAccessProfileLoaded(false);
    const loadAccessProfile = async () => {
      try {
        const accessProfileDocId = firebaseAuthUid;
        if (import.meta.env.DEV) {
          console.debug('[access-profile.guard] loading access profile', {
            firebaseAuthUid,
            currentUserAuthUid: currentUser?.authUid || null,
            currentUserId: currentUser?.id || null,
            accessProfileDocId,
          });
        }
        const snap = await getDoc(doc(db, 'access_profiles', accessProfileDocId));
        if (cancelled) return;
        const profile = snap.exists() ? snap.data() as RulesAccessProfile : null;
        console.debug('[access-profile.guard] access profile loaded', {
          accessProfileDocId,
          exists: snap.exists(),
          role: profile?.role || null,
          unitIds: profile ? normalizeUserUnitIds(profile) : [],
          appUserId: profile?.appUserId || null,
          canonicalUserId: profile?.canonicalUserId || null,
        });
        if (!snap.exists()) {
          console.warn('[access-profile.guard] missing access profile; using current user fallback for P0 only', {
            firebaseAuthUid,
            currentUserAuthUid: currentUser?.authUid || null,
            currentUserId: currentUser.id,
            accessProfileDocId,
            currentUserUnitIds: currentUser.unitIds || [],
          });
          setAccessProfile(null);
          return;
        }
        setAccessProfile({
          ...profile,
          unitIds: normalizeUserUnitIds(profile),
        });
      } catch (error) {
        if (cancelled) return;
        console.error('[access-profile.guard] failed to load access profile', {
          firebaseAuthUid,
          currentUserAuthUid: currentUser?.authUid || null,
          currentUserId: currentUser.id,
          accessProfileDocId: firebaseAuthUid,
          error,
        });
        setAccessProfile(null);
      } finally {
        if (!cancelled) setIsAccessProfileLoaded(true);
      }
    };
    void loadAccessProfile();

    return () => {
      cancelled = true;
    };
  }, [isDemoMode, firebaseAuthUid, currentUser?.authUid, currentUser?.id, currentUserUnitKey]);

  const authorizationUnitIds = useMemo(() => {
    if (!currentUser) return [];
    const fallbackUnitIds = normalizeUserUnitIds(currentUser);
    if (!isAccessProfileLoaded) return [];
    if (!accessProfile) return fallbackUnitIds;

    const profileUnitIds = normalizeUserUnitIds(accessProfile);
    if (profileUnitIds.length > 0) return profileUnitIds;

    if (fallbackUnitIds.length > 0 && import.meta.env.DEV) {
      console.warn('[access-profile.guard] profile missing unit ids; using current user units for dashboard request scope', {
        authUid: auth.currentUser?.uid || null,
        currentUserId: currentUser.id,
        accessProfileAppUserId: accessProfile.appUserId || null,
        accessProfileCanonicalUserId: accessProfile.canonicalUserId || null,
        fallbackUnitIds,
      });
    }

    return fallbackUnitIds;
  }, [accessProfile, currentUser, isAccessProfileLoaded]);

  const authorizationUnitIdsKey = useMemo(
    () => authorizationUnitIds.slice().sort().join('|'),
    [authorizationUnitIds]
  );

  const effectiveCurrentUser = useMemo<User | null>(() => {
    if (!currentUser) return null;
    if (!isAccessProfileLoaded) return currentUser;
    if (!accessProfile) return currentUser;
    const role = accessProfile.role || currentUser.role;
    return {
      ...currentUser,
      role,
      unitIds: authorizationUnitIds,
      authUid: firebaseAuthUid || accessProfile.authUid || currentUser.authUid,
      accessProfileVersion: typeof accessProfile.accessProfileVersion === 'number'
        ? accessProfile.accessProfileVersion
        : currentUser.accessProfileVersion,
    };
  }, [accessProfile, authorizationUnitIds, currentUser, firebaseAuthUid, isAccessProfileLoaded]);
  const isAccessProfileBlocked = Boolean(accessProfile?.disabled === true || accessProfile?.suspended === true);

  useEffect(() => {
    if (appState !== 'dashboard') return;
    if (!isAccessProfileLoaded || !isAccessProfileBlocked) return;
    setFirestoreError('A felhasználói hozzáférés jelenleg le van tiltva.');
  }, [appState, isAccessProfileBlocked, isAccessProfileLoaded]);

  // ---------- Global theme Firestore listener (ONLY after currentUser exists) ----------
  useEffect(() => {
    if (isDemoMode) return;
    if (!currentUser?.id) return;

    const unsub = onSnapshot(
      doc(db, 'global_settings', 'theme'),
      snap => {
        if (!snap.exists()) return;
        const data = snap.data() as any;

        // igazítsd a te global_settings/theme struktúrádhoz
        if (data?.bases) setThemeBases(data.bases as ThemeBases);
        if (data?.mode) setThemeMode(data.mode as ThemeMode);
      },
      err => {
        console.warn('Theme load info:', err?.message || String(err));
      }
    );

    return () => unsub();
  }, [isDemoMode, currentUser?.id]);

  useEffect(() => {
    if (isDemoMode || appState !== 'dashboard' || effectiveCurrentUser?.role === 'Admin') return undefined;

    const requestPermissionRefresh = () => setPermissionRefreshNonce(nonce => nonce + 1);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestPermissionRefresh();
      }
    };

    window.addEventListener(PERMISSION_REFRESH_EVENT, requestPermissionRefresh);
    window.addEventListener('focus', requestPermissionRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener(PERMISSION_REFRESH_EVENT, requestPermissionRefresh);
      window.removeEventListener('focus', requestPermissionRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appState, effectiveCurrentUser?.role, currentUser?.role, isDemoMode]);

  // ---------- Minimal listeners needed for dashboard shell (NO denied) ----------
  // Admin: users + permissions
  // Units: Admin => all, non-admin => only documentId in unitIds
  useEffect(() => {
    if (isDemoMode) return;
    if (appState !== 'dashboard') return;
    if (!effectiveCurrentUser) return;
    if (!effectiveCurrentUser?.id) return;
    if (!isAccessProfileLoaded) return;
    if (isAccessProfileBlocked) {
      setAllUsers([]);
      setAllUnits([]);
      setPermissions({});
      setUnitPermissions({});
      return;
    }

    const isAdmin = effectiveCurrentUser.role === 'Admin';
    const unitIds = authorizationUnitIds;
    console.debug('[claimed-user.debug.dashboard-shell]', {
      currentUserId: effectiveCurrentUser.id,
      authUid: auth.currentUser?.uid || null,
      profileAppUserId: accessProfile?.appUserId || null,
      profileCanonicalUserId: accessProfile?.canonicalUserId || null,
      profileDisabled: accessProfile?.disabled === true,
      profileSuspended: accessProfile?.suspended === true,
      currentUserUnitIds: currentUser?.unitIds || [],
      profileUnitIds: accessProfile?.unitIds || [],
      listenerUnitIds: unitIds,
    });

    let isCancelled = false;
    let unsubPerms: undefined | (() => void);
    let unsubUnits: undefined | (() => void);

    if (isAdmin) {
      const loadUsers = async () => {
        try {
          const users = await listUsersViaApi();
          if (!isCancelled) {
            setAllUsers(users as User[]);
          }
        } catch (error) {
          if (!isCancelled) {
            console.error('Failed to load users via API for dashboard shell', error);
            setAllUsers([]);
          }
        }
      };
      void loadUsers();

      unsubPerms = onSnapshot(
        collection(db, 'permissions'),
        snap => {
          const perms: RolePermissions = {};
          snap.forEach(d => {
            perms[d.id as User['role']] = d.data() as Partial<Permissions>;
          });
          setPermissions(perms);
        },
        error => {
          logAuthDiagnostic('snapshot:error', {
            listenerName: 'permissions',
            error: authErrorDetails(error),
          });
          console.error('permissions listener error:', authErrorDetails(error));
        }
      );

      unsubUnits = onSnapshot(
        collection(db, 'units'),
        snap => {
          setAllUnits(snap.docs.map(d => ({ id: d.id, ...d.data() } as Unit)));
        },
        error => {
          logAuthDiagnostic('snapshot:error', {
            listenerName: 'units',
            error: authErrorDetails(error),
          });
          console.error('units listener error:', authErrorDetails(error));
        }
      );
    } else {
      // non-admin cannot read the full units collection anymore
      if (!unitIds.length) {
        setAllUnits([]);
      } else {
        const qUnits = query(collection(db, 'units'), where(documentId(), 'in', unitIds.slice(0, 10)));
        unsubUnits = onSnapshot(
          qUnits,
          snap => {
            setAllUnits(snap.docs.map(d => ({ id: d.id, ...d.data() } as Unit)));
          },
          error => {
            logAuthDiagnostic('snapshot:error', {
              listenerName: 'units',
              error: authErrorDetails(error),
            });
            console.error('units listener error:', authErrorDetails(error));
          }
        );
      }
      setAllUsers([]); // non-admin: no global staff list

      const loadRolePermissionDocuments = async () => {
        try {
          const [globalPerms, unitPermDocs] = await Promise.all([
            getGlobalPermissionsViaApi(),
            Promise.all(
              unitIds.map(async unitId => {
                try {
                  const doc = await getUnitPermissionsViaApi(unitId);
                  return [unitId, doc] as const;
                } catch (error) {
                  console.error('Failed to load unit permission document via API', { unitId, error });
                  return [unitId, null] as const;
                }
              })
            ),
          ]);
          if (isCancelled) return;
          setPermissions(globalPerms || {});
          setUnitPermissions(Object.fromEntries(unitPermDocs.filter(([, doc]) => Boolean(doc))));
        } catch (error) {
          if (!isCancelled) {
            console.error('Failed to load role permission documents via API', error);
            setPermissions({});
            setUnitPermissions({});
          }
        }
      };
      void loadRolePermissionDocuments();
    }

    return () => {
      isCancelled = true;
      unsubPerms?.();
      unsubUnits?.();
    };
  }, [
    appState,
    isDemoMode,
    effectiveCurrentUser?.id,
    effectiveCurrentUser?.role,
    isAccessProfileLoaded,
    isAccessProfileBlocked,
    authorizationUnitIdsKey,
    permissionRefreshNonce,
  ]);


  const schedulerShiftUnitIds = useMemo(
    () => (effectiveCurrentUser ? authorizationUnitIds.slice(0, 10) : []),
    [authorizationUnitIdsKey, effectiveCurrentUser?.id]
  );
  const schedulerShiftUnitIdsKey = useMemo(
    () => schedulerShiftUnitIds.slice().sort().join('|'),
    [schedulerShiftUnitIds]
  );
  const currentSchedulerShiftScopeKey = useMemo(
    () => buildSchedulerRefreshScopeKey({
      unitIds: schedulerShiftUnitIds,
      userId: currentUser?.id || null,
    }),
    [currentUser?.id, schedulerShiftUnitIdsKey]
  );
  const hasUsableCurrentScopeSchedule =
    hasLoadedShiftsOnce &&
    loadedSchedulerShiftScopeKey === currentSchedulerShiftScopeKey;

  useEffect(() => {
    if (isDemoMode) return undefined;
    if (appState !== 'dashboard') return undefined;
    if (!currentUser) return undefined;
    if (!effectiveCurrentUser || !effectiveCurrentUser?.id) return undefined;
    if (!isAccessProfileLoaded) return undefined;
    if (isAccessProfileBlocked) return undefined;
    if (dashboardActiveAppRef.current === 'beosztas') {
      setDashboardTodayShiftStatus('loaded');
      return undefined;
    }

    const todayDateKey = toDateKey(new Date());
    const shiftUnitIds = authorizationUnitIds;
    const scopeKey = buildSchedulerRefreshScopeKey({
      unitIds: shiftUnitIds,
      userId: currentUser.id,
    });
    const todayWindow = { fromDateKey: todayDateKey, toDateKey: todayDateKey };
    const bootstrapState = dashboardTodayShiftBootstrapRef.current;
    const hasSchedulerHydratedToday = isWindowHydrated(schedulerHydratedShiftWindowsRef.current, todayWindow);
    const hasDashboardHydratedToday = bootstrapState.scopeKey === scopeKey && (bootstrapState.completed || bootstrapState.inFlight);

    if (shiftUnitIds.length === 0 || hasSchedulerHydratedToday || hasDashboardHydratedToday) {
      setDashboardTodayShiftStatus('loaded');
      setIsShiftsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let isCancelled = false;
    dashboardTodayShiftBootstrapRef.current = { scopeKey, inFlight: true, completed: false };
    setDashboardTodayShiftStatus('loading');
    setIsShiftsLoading(true);

    void listDashboardShifts(shiftUnitIds, controller.signal, event => {
      if (!import.meta.env.DEV) return;
      console.debug('[dashboard.today-shifts.bootstrap]', {
        stage: event.stage,
        statusCode: event.statusCode ?? null,
        payloadCount: event.payloadCount ?? null,
        from: event.fromDateKey ?? todayDateKey,
        to: event.toDateKey ?? todayDateKey,
        unitCount: shiftUnitIds.length,
      });
    }, { mode: 'window', fromDateKey: todayDateKey, toDateKey: todayDateKey }).then(todayShifts => {
      if (isCancelled || controller.signal.aborted) return;
      setShifts(previous => mergeShiftsById(previous, todayShifts));
      dashboardTodayShiftBootstrapRef.current = { scopeKey, inFlight: false, completed: true };
      setDashboardTodayShiftStatus('loaded');
    }).catch(error => {
      if (isCancelled || controller.signal.aborted || isAbortLikeError(error)) return;
      dashboardTodayShiftBootstrapRef.current = { scopeKey, inFlight: false, completed: false };
      setDashboardTodayShiftStatus('error');
      console.error('dashboard today shifts bootstrap error:', {
        error,
        statusCode: (error as any)?.statusCode,
        path: (error as any)?.path,
        responseBody: (error as any)?.responseBody,
      });
    }).finally(() => {
      if (isCancelled || controller.signal.aborted) return;
      setIsShiftsLoading(false);
    });

    return () => {
      isCancelled = true;
      controller.abort();
      if (dashboardTodayShiftBootstrapRef.current.scopeKey === scopeKey && dashboardTodayShiftBootstrapRef.current.inFlight) {
        dashboardTodayShiftBootstrapRef.current = { scopeKey: null, inFlight: false, completed: false };
        setDashboardTodayShiftStatus('idle');
      }
    };
  }, [
    appState,
    currentUser?.id,
    effectiveCurrentUser?.id,
    isAccessProfileLoaded,
    isAccessProfileBlocked,
    isDemoMode,
    authorizationUnitIdsKey,
  ]);

  useEffect(() => {
    if (isDemoMode) return undefined;
    if (appState !== 'dashboard') return undefined;
    if (!currentUser) return undefined;
    if (!effectiveCurrentUser || !effectiveCurrentUser?.id) return undefined;
    if (!isAccessProfileLoaded) return undefined;
    if (isAccessProfileBlocked) return undefined;

    // Scheduler shift refresh controller ownership is intentionally narrower than
    // the broad dashboard listener effect. It is scoped only by dashboard/auth
    // readiness, current user identity, and the effective shift unit scope so
    // permissions/unitPermissions/allUnits metadata churn cannot recreate the
    // controller and fire a duplicate app_open bootstrap refresh.
    const shiftUnitIds = schedulerShiftUnitIds;
    const shiftScopeKey = buildSchedulerRefreshScopeKey({
      unitIds: shiftUnitIds,
      userId: currentUser.id,
    });
    if (loadedSchedulerShiftScopeKeyRef.current !== shiftScopeKey) {
      setHasLoadedShiftsOnce(false);
      hasLoadedShiftsOnceRef.current = false;
      setLoadedSchedulerShiftScopeKey(null);
      loadedSchedulerShiftScopeKeyRef.current = null;
    }
    const shiftsQueryShape = {
      path: '/api/shifts',
      constraints: [{ field: 'unitIds', op: 'csv', value: shiftUnitIds }],
    };
    console.debug('[dashboard.listener.scope]', {
      listenerName: 'shifts:api',
      authUid: auth.currentUser?.uid || null,
      currentUserId: currentUser.id,
      finalQueryUnitIds: shiftUnitIds,
      effectiveRole: effectiveCurrentUser.role,
      profileRole: accessProfile?.role || null,
      accessProfileUnitIds: accessProfile?.unitIds || [],
      profileScopedUnitIds: authorizationUnitIds,
      dashboardQueryUnitIds: schedulerShiftUnitIds,
      queryShape: shiftsQueryShape,
    });
    console.debug('[claimed-user.debug.shifts-query]', {
      currentUserId: currentUser.id,
      authUid: auth.currentUser?.uid || null,
      profileAppUserId: accessProfile?.appUserId || null,
      profileCanonicalUserId: accessProfile?.canonicalUserId || null,
      profileDisabled: accessProfile?.disabled === true,
      profileSuspended: accessProfile?.suspended === true,
      currentUserUnitIds: currentUser?.unitIds || [],
      profileUnitIds: accessProfile?.unitIds || [],
      shiftUnitIds,
    });

    const onShiftsError = (err: any) => {
      if (isAbortLikeError(err)) return;
      logAuthDiagnostic('snapshot:error', {
        listenerName: 'shifts:api',
        error: authErrorDetails(err),
        scope: {
          listenerName: 'shifts:api',
          authUid: auth.currentUser?.uid || null,
          currentUserId: currentUser.id,
          finalQueryUnitIds: shiftUnitIds,
          effectiveRole: effectiveCurrentUser.role,
          profileRole: accessProfile?.role || null,
          accessProfileUnitIds: accessProfile?.unitIds || [],
          profileScopedUnitIds: authorizationUnitIds,
          dashboardQueryUnitIds: schedulerShiftUnitIds,
          queryShape: shiftsQueryShape,
        },
      });
      console.error('shifts:api listener error:', {
        error: err,
        statusCode: err?.statusCode,
        path: err?.path,
        responseBody: err?.responseBody,
        listenerName: 'shifts:api',
        authUid: auth.currentUser?.uid || null,
        currentUserId: currentUser.id,
        finalQueryUnitIds: shiftUnitIds,
        effectiveRole: effectiveCurrentUser.role,
        profileRole: accessProfile?.role || null,
        accessProfileUnitIds: accessProfile?.unitIds || [],
        profileScopedUnitIds: authorizationUnitIds,
        dashboardQueryUnitIds: schedulerShiftUnitIds,
        queryShape: shiftsQueryShape,
      });
      const rawCode = typeof err?.code === 'string' ? err.code : '';
      const normalizedCode = rawCode.replace(/^functions\//, '');
      const msg = String(err?.message || '').toLowerCase();
      if (normalizedCode === 'failed-precondition' || msg.includes('index')) {
        setFirestoreError('Adatbázis index frissítés folyamatban...');
        return;
      }
      if (normalizedCode === 'permission-denied') return;
      if (normalizedCode === 'internal' || normalizedCode === 'unknown') {
        setFirestoreError('Átmeneti adatbetöltési hiba történt. Próbáld újra később.');
      }
    };

    let isShiftsFetchCancelled = false;
    schedulerRefreshUnitIdsRef.current = shiftUnitIds;
    schedulerHydratedShiftWindowsRef.current = [];
    schedulerRefreshWindowBySequenceRef.current.clear();
    schedulerBootstrapRefreshDedupeRef.current = {
      inFlightScopeKey: null,
      completedScopeKey: null,
      firstPaintCompleted: false,
    };
    schedulerRefreshControllerRef.current = new SchedulerRefreshController({
      getUnitIds: () => schedulerRefreshUnitIdsRef.current,
      onTimeoutCleanup: () => {
        if (isShiftsFetchCancelled) return;
        setIsShiftsLoading(false);
        setIsShiftsRefreshing(false);
      },
      diagnostics: event => {
        schedulerBootstrapProfiler.recordRefresh({
          type: event.type,
          reason: event.reason,
          mode: isSoftSchedulerRefreshReason(event.reason) ? 'soft' : 'hard',
          durationMs: event.duration,
        });
        if (!import.meta.env.DEV) return;
        console.debug(`[scheduler refresh ${event.type}]`, {
          reason: event.reason,
          duration: event.duration,
          affectedUnitIds: event.affectedUnitIds,
          scopeKey: shiftScopeKey,
        });
      },
      refresh: async ({ reason, unitIds, signal, requestSequence }) => {
        const ownedRequestSequence = requestSequence ?? 0;
        latestStartedSchedulerRefreshSequenceRef.current = ownedRequestSequence;
        const isSoftRefresh = isSoftSchedulerRefreshReason(reason);
        const hasUsableLoadedScope =
          hasLoadedShiftsOnceRef.current &&
          loadedSchedulerShiftScopeKeyRef.current === shiftScopeKey;
        const hasVisibleSchedule = hasUsableLoadedScope;
        const shouldShowBlockingShiftLoading = !isSoftRefresh || !hasVisibleSchedule;
        const shouldShowBackgroundShiftRefresh = isSoftRefresh && hasVisibleSchedule;
        if (shouldShowBlockingShiftLoading) setIsShiftsLoading(true);
        if (shouldShowBackgroundShiftRefresh) setIsShiftsRefreshing(true);
        try {
          const sequenceWindowRequest = schedulerRefreshWindowBySequenceRef.current.get(ownedRequestSequence);
          const sequenceWindow = sequenceWindowRequest?.window;
          const shouldUseWindowedBootstrap = reason === 'app_open' && !hasUsableLoadedScope && Boolean(sequenceWindow);
          const shouldUseWindowedRange = reason === 'week_change' && Boolean(sequenceWindow);
          const shiftReadWindow = shouldUseWindowedBootstrap || shouldUseWindowedRange
            ? sequenceWindow
            : undefined;
          if (reason === 'app_open' && !hasUsableLoadedScope && !shiftReadWindow) {
            schedulerBootstrapProfiler.recordRefresh({
              type: 'skipped',
              reason,
              mode: 'hard',
              detail: 'shift-api:app-open-missing-bound-visible-window',
            });
            return;
          }
          const shiftReadOptions: DashboardShiftReadOptions = shiftReadWindow
            ? { mode: 'window', fromDateKey: shiftReadWindow.fromDateKey, toDateKey: shiftReadWindow.toDateKey }
            : { mode: 'full' };
          const shiftReadMode = shiftReadOptions.mode || 'full';
          const apiShifts = await listDashboardShifts(unitIds, signal, event => {
            const stage = event.mode === 'window' && event.stage === 'request-started'
              ? 'window-request-started'
              : event.mode === 'window' && event.stage === 'backend-response-received'
                ? 'window-response-received'
                : event.stage;
            schedulerBootstrapProfiler.recordRefresh({
              type: event.stage === 'request-started' ? 'start' : 'complete',
              reason,
              mode: isSoftRefresh ? 'soft' : 'hard',
              durationMs: event.durationMs,
              detail: `shift-api:${stage}:status=${event.statusCode ?? 'n/a'}:count=${event.payloadCount ?? 'n/a'}:mode=${event.mode ?? shiftReadMode}:from=${event.fromDateKey ?? shiftReadWindow?.fromDateKey ?? 'n/a'}:to=${event.toDateKey ?? shiftReadWindow?.toDateKey ?? 'n/a'}`,
            });
          }, shiftReadOptions);
          if (isShiftsFetchCancelled || signal.aborted) return;
          const responseDecision = resolveSchedulerRefreshResponseDecision({
            reason,
            previousVisibleShifts: shiftReadMode === 'window' ? [] : shiftsRef.current,
            apiShifts,
            hasHydratedOnce: hasUsableLoadedScope,
            requestSequence: ownedRequestSequence,
            latestStartedSequence: latestStartedSchedulerRefreshSequenceRef.current,
          });
          if (responseDecision.action === 'ignore_old_sequence_response') {
            if (import.meta.env.DEV) {
              console.debug('[scheduler refresh response ignored:old_sequence]', {
                reason,
                requestSequence: ownedRequestSequence,
                latestStartedSequence: latestStartedSchedulerRefreshSequenceRef.current,
                apiShiftCount: apiShifts.length,
              });
            }
            return;
          }
          if (responseDecision.action === 'ignore_stale_empty_soft_response') {
            setSchedulerRefreshWarning(responseDecision.warningMessage);
            if (import.meta.env.DEV) {
              console.debug('[scheduler refresh response ignored:stale_empty_soft]', {
                reason,
                requestSequence: ownedRequestSequence,
                previousShiftCount: shiftsRef.current.length,
                apiShiftCount: apiShifts.length,
              });
            }
            return;
          }
          setSchedulerRefreshWarning(null);
          if (shiftReadMode === 'window' && shiftReadWindow) {
            setShifts(previous => mergeShiftsById(previous, apiShifts));
            schedulerHydratedShiftWindowsRef.current = [
              ...schedulerHydratedShiftWindowsRef.current.filter(range => !windowContains(shiftReadWindow, range)),
              shiftReadWindow,
            ];
          } else {
            setShifts(apiShifts);
          }
          schedulerBootstrapProfiler.recordRefresh({
            type: 'complete',
            reason,
            mode: isSoftRefresh ? 'soft' : 'hard',
            detail: `shift-api:${shiftReadMode === 'window' ? 'window-setShifts-applied' : 'setShifts-applied'}:count=${apiShifts.length}:mode=${shiftReadMode}:from=${shiftReadWindow?.fromDateKey ?? 'n/a'}:to=${shiftReadWindow?.toDateKey ?? 'n/a'}`,
          });
          setHasLoadedShiftsOnce(true);
          hasLoadedShiftsOnceRef.current = true;
          setLoadedSchedulerShiftScopeKey(shiftScopeKey);
          loadedSchedulerShiftScopeKeyRef.current = shiftScopeKey;
          if (import.meta.env.DEV) {
            const publishedShifts = apiShifts.filter(shift => shift.status === 'published');
            console.debug('[scheduler refresh response applied]', {
              reason,
              requestSequence: ownedRequestSequence,
              apiShiftCount: apiShifts.length,
              publishedCount: publishedShifts.length,
              unitIds,
              firstPublishedIds: publishedShifts.slice(0, 5).map(shift => shift.id),
              firstPublishedSourceShiftIds: publishedShifts.slice(0, 5).map(shift => (shift as Shift & { sourceShiftId?: string }).sourceShiftId || null),
              firstPublishedUnitDayStatus: publishedShifts.slice(0, 5).map(shift => ({
                id: shift.id,
                unitId: shift.unitId || null,
                dayKey: shift.dayKey || null,
                status: shift.status || null,
              })),
            });
          }
        } catch (error) {
          if (isShiftsFetchCancelled || signal.aborted || isAbortLikeError(error)) {
            const replacementStarted = ownedRequestSequence < latestStartedSchedulerRefreshSequenceRef.current;
            schedulerBootstrapProfiler.recordAbort({
              operation: 'listDashboardShifts',
              module: 'App.refreshSchedulerShifts',
              reason: signal.aborted ? 'refresh-signal-aborted' : isShiftsFetchCancelled ? 'scheduler-refresh-disposed' : replacementStarted ? 'superseded-request-sequence' : 'abort-like-error-without-explicit-proof',
              classification: signal.aborted || isShiftsFetchCancelled || replacementStarted ? 'expected' : 'unknown',
              replacementStarted,
            });
            return;
          }
          onShiftsError(error);
          if (shouldShowBlockingShiftLoading && !hasVisibleSchedule) setShifts([]);
          throw error;
        } finally {
          schedulerRefreshWindowBySequenceRef.current.delete(ownedRequestSequence);
          const isLatestRefreshSequence = ownedRequestSequence === latestStartedSchedulerRefreshSequenceRef.current;
          if (!isShiftsFetchCancelled && isLatestRefreshSequence && shouldShowBlockingShiftLoading) setIsShiftsLoading(false);
          if (!isShiftsFetchCancelled && isLatestRefreshSequence && shouldShowBackgroundShiftRefresh) setIsShiftsRefreshing(false);
        }
      },
    });
    maybeStartSchedulerWindowedAppOpen();

    return () => {
      isShiftsFetchCancelled = true;
      schedulerAppOpenBootstrapRef.current = resolveSchedulerAppOpenLifecycleCancelled({
        scopeKey: shiftScopeKey,
        state: schedulerAppOpenBootstrapRef.current,
      });
      schedulerRefreshControllerRef.current?.dispose();
      schedulerRefreshControllerRef.current = null;
      schedulerBootstrapRefreshDedupeRef.current = {
        inFlightScopeKey: null,
        completedScopeKey: null,
        firstPaintCompleted: false,
      };
      schedulerHydratedShiftWindowsRef.current = [];
      schedulerRefreshWindowBySequenceRef.current.clear();
      setIsShiftsLoading(false);
      setIsShiftsRefreshing(false);
    };
  }, [
    appState,
    currentUser?.id,
    effectiveCurrentUser?.id,
    isAccessProfileLoaded,
    isAccessProfileBlocked,
    isDemoMode,
    schedulerShiftUnitIdsKey,
    refreshSchedulerShifts,
    maybeStartSchedulerWindowedAppOpen,
  ]);

  // ---------- Authenticated data listeners (NO collectionGroup for top-level polls/feedback) ----------
  useEffect(() => {
    if (isDemoMode) return;
    if (appState !== 'dashboard') return;
    if (!currentUser) return;
    if (!effectiveCurrentUser || !effectiveCurrentUser?.id) return;
    if (!isAccessProfileLoaded) return;
    if (isAccessProfileBlocked) {
      setFirestoreError('A felhasználói hozzáférés jelenleg le van tiltva.');
      setRequests([]);
      setShifts([]);
      setHasLoadedShiftsOnce(false);
      hasLoadedShiftsOnceRef.current = false;
      setLoadedSchedulerShiftScopeKey(null);
      loadedSchedulerShiftScopeKeyRef.current = null;
      setIsShiftsLoading(false);
      setIsShiftsRefreshing(false);
      setTodos([]);
      setAdminTodos([]);
      setTimeEntries([]);
      setFeedbackList([]);
      setPolls([]);
      return;
    }

    setFirestoreError(null);

    const profileScopedUnitIds = authorizationUnitIds;
    const dashboardQueryUnitIds = profileScopedUnitIds.slice(0, 10);
    const isSuperAdmin = effectiveCurrentUser.role === 'Admin';
    const adminScopedUnitIds = dashboardQueryUnitIds;
    const buildListenerScopeLog = (
      listenerName: string,
      finalQueryUnitIds: string[] | 'self',
      queryShape?: Record<string, unknown>
    ) => ({
      listenerName,
      authUid: auth.currentUser?.uid || null,
      currentUserId: currentUser.id,
      finalQueryUnitIds,
      effectiveRole: effectiveCurrentUser.role,
      profileRole: accessProfile?.role || null,
      accessProfileUnitIds: accessProfile?.unitIds || [],
      profileScopedUnitIds,
      dashboardQueryUnitIds,
      queryShape,
    });
    const logListenerScope = (
      listenerName: string,
      finalQueryUnitIds: string[] | 'self',
      queryShape?: Record<string, unknown>
    ) => {
      console.debug('[dashboard.listener.scope]', buildListenerScopeLog(listenerName, finalQueryUnitIds, queryShape));
    };
    console.debug('[dashboard.listeners.scope]', {
      profileRole: accessProfile?.role || null,
      effectiveRole: effectiveCurrentUser.role,
      profileUnitIds: accessProfile?.unitIds || [],
      activeUnitIds: currentUser?.unitIds || [],
      finalListenerUnitIds: dashboardQueryUnitIds,
    });
    const onIndexOrPerms = (
      listenerName: string,
      finalQueryUnitIds: string[] | 'self' = [],
      queryShape?: Record<string, unknown>
    ) => (err: any) => {
      if (isAbortLikeError(err)) return;
      logAuthDiagnostic('snapshot:error', {
        listenerName,
        error: authErrorDetails(err),
        scope: buildListenerScopeLog(listenerName, finalQueryUnitIds, queryShape),
      });

      console.error(`${listenerName} listener error:`, {
        error: err,
        statusCode: err?.statusCode,
        path: err?.path,
        responseBody: err?.responseBody,
        ...buildListenerScopeLog(listenerName, finalQueryUnitIds, queryShape),
      });
      const rawCode = typeof err?.code === 'string' ? err.code : '';
      const normalizedCode = rawCode.replace(/^functions\//, '');
      const msg = String(err?.message || '').toLowerCase();

      if (normalizedCode === 'failed-precondition' || msg.includes('index')) {
        setFirestoreError('Adatbázis index frissítés folyamatban...');
        return;
      }

      if (normalizedCode === 'permission-denied') {
        // Optional dashboard listeners are best-effort. A single denied widget feed
        // must not poison the whole dashboard shell.
        return;
      }

      if (normalizedCode === 'internal' || normalizedCode === 'unknown') {
        setFirestoreError('Átmeneti adatbetöltési hiba történt. Próbáld újra később.');
      }
      // ne dobd ki a usert: a dashboard menjen minimál módban is
    };

    // unit_permissions: RULES nálad Admin-only → csak admin hallgassa
    const unsubUnitPerms: Array<() => void> = [];
    if (isSuperAdmin) {
      authorizationUnitIds.forEach(unitId => {
        unsubUnitPerms.push(
          onSnapshot(
            doc(db, 'unit_permissions', unitId),
            snap => {
              if (snap.exists()) setUnitPermissions(prev => ({ ...prev, [unitId]: snap.data() }));
            },
            onIndexOrPerms('unit_permissions')
          )
        );
      });
    }

    // todos
    const todoUnitIds = isSuperAdmin
      ? adminScopedUnitIds
      : profileScopedUnitIds.slice(0, 10);
    const todosQueryRef: Query<DocumentData> | null = todoUnitIds.length > 0
      ? query(collection(db, 'todos'), where('unitId', 'in', todoUnitIds))
      : null;
    const todosQueryShape = {
      path: '/todos',
      constraints: todoUnitIds.length > 0
        ? [{ field: 'unitId', op: 'in', value: todoUnitIds }]
        : [{ field: '__name__', op: '==', value: '__no_matching_todo__' }],
    };
    logListenerScope('todos', todoUnitIds, todosQueryShape);
    if (!todosQueryRef) setTodos([]);
    const unsubTodos = todosQueryRef ? onSnapshot(
      todosQueryRef,
      snap => setTodos(snap.docs.map(d => ({ id: d.id, ...d.data() } as Todo))),
      onIndexOrPerms('todos', todoUnitIds, todosQueryShape)
    ) : () => {};

    // admin_todos
    let unsubAdminTodos: () => void = () => {};
    if (isSuperAdmin) {
      unsubAdminTodos = onSnapshot(
        collection(db, 'admin_todos'),
        snap => setAdminTodos(snap.docs.map(d => ({ id: d.id, ...d.data() } as Todo))),
        onIndexOrPerms('admin_todos')
      );
    } else {
      setAdminTodos([]);
    }

    // shifts
    // Dashboard shift widget bootstrap uses the backend shift API, not a realtime
    // top-level Firestore listener.
    // const shiftUnitIds = isSuperAdmin ? adminScopedUnitIds : dashboardQueryUnitIds
    // listDashboardShifts(unitIds, signal)
    // path: '/api/shifts'
    // field: 'unitIds', op: 'csv', value: shiftUnitIds
    // onIndexOrPerms('shifts:api', unitIds, shiftsQueryShape)

    // requests
    // Security hardening: this dashboard read is a bounded backend fetch, not a realtime
    // top-level Firestore listener. If realtime is needed later, add a backend-owned
    // SSE/WebSocket or explicit refresh/polling path instead of reopening Firestore list.
    const leaveRequestScope = resolveLeaveRequestPermissionScope({
      currentUser: effectiveCurrentUser,
      allPermissions: permissions,
      unitPermissions,
      allUnits,
    });
    const reviewableRequestUnitIds = Object.values(leaveRequestScope.byUnitId)
      .filter(permission =>
        permission.canViewAll ||
        permission.canViewTeam ||
        permission.canAccessAdminList ||
        permission.canReview
      )
      .map(permission => permission.unitId);
    const ownRequestUnitIds = Object.values(leaveRequestScope.byUnitId)
      .filter(permission => permission.canCreate)
      .map(permission => permission.unitId);
    const profileReviewableRequestUnitIds = intersectUnitIds(reviewableRequestUnitIds, profileScopedUnitIds);
    const profileOwnRequestUnitIds = intersectUnitIds(ownRequestUnitIds, profileScopedUnitIds);
    const requestIdentityIds = Array.from(new Set([
      ...getCurrentUserIdentityIds(currentUser),
      accessProfile?.appUserId,
      accessProfile?.canonicalUserId,
      (currentUser as User & { appUserId?: string; canonicalUserId?: string; linkedUserId?: string }).appUserId,
      (currentUser as User & { appUserId?: string; canonicalUserId?: string; linkedUserId?: string }).canonicalUserId,
      (currentUser as User & { appUserId?: string; canonicalUserId?: string; linkedUserId?: string }).linkedUserId,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0))).slice(0, 10);
    const shouldIncludeOwnRequestUnits =
      requestIdentityIds.length > 0 && profileOwnRequestUnitIds.length > 0;
    const requestUnsubscribers: Array<() => void> = [];
    const requestApiUnitIds = isSuperAdmin
      ? adminScopedUnitIds
      : Array.from(new Set([
        ...profileReviewableRequestUnitIds,
        ...(shouldIncludeOwnRequestUnits ? profileOwnRequestUnitIds : []),
      ]));

    if (import.meta.env.DEV) {
      console.debug('leave-request-read-scope', {
        authUid: auth.currentUser?.uid || null,
        currentUserId: currentUser.id,
        currentUserRole: effectiveCurrentUser.role,
        profileAppUserId: accessProfile?.appUserId || null,
        profileCanonicalUserId: accessProfile?.canonicalUserId || null,
        profileDisabled: accessProfile?.disabled === true,
        profileSuspended: accessProfile?.suspended === true,
        currentUserUnitIds: currentUser.unitIds || [],
        profileUnitIds: accessProfile?.unitIds || [],
        profileScopedUnitIds,
        reviewableRequestUnitIds,
        ownRequestUnitIds,
        listenerUnitIds: isSuperAdmin
          ? adminScopedUnitIds
          : Array.from(new Set([
            ...profileReviewableRequestUnitIds,
            ...(shouldIncludeOwnRequestUnits ? profileOwnRequestUnitIds : []),
          ])),
        permissionScopeByUnitId: leaveRequestScope.byUnitId,
        requestIdentityIds,
        shouldIncludeOwnRequestUnits,
        requestApiUnitIds,
      });
    }
    const requestsQueryShape = {
      path: '/api/leave-requests',
      constraints: [{ field: 'unitIds', op: 'csv', value: requestApiUnitIds }],
    };
    logListenerScope('requests:api', requestApiUnitIds, requestsQueryShape);
    if (requestIdentityIds.length > 0) {
      console.warn('requests own-scope listener skipped for P0: dashboard request reads use backend unit-scoped API', {
        authUid: auth.currentUser?.uid || null,
        currentUserId: currentUser.id,
        requestIdentityIds,
      });
    }
    if (requestApiUnitIds.length === 0) {
      console.warn('requests query skipped: missing current user request scope');
      setRequests([]);
    } else {
      let isRequestsFetchCancelled = false;
      void listDashboardRequests(requestApiUnitIds)
        .then(apiRequests => {
          if (isRequestsFetchCancelled) return;
          const visibleRequests = apiRequests.filter(request =>
            isSuperAdmin ||
            (request.unitId ? profileReviewableRequestUnitIds.includes(request.unitId) : false) ||
            requestIdentityIds.includes(request.userId) ||
            (request.createdBy ? requestIdentityIds.includes(request.createdBy) : false)
          );
          setRequests(visibleRequests);
        })
        .catch(error => {
          if (isRequestsFetchCancelled) return;
          onIndexOrPerms('requests:api', requestApiUnitIds, requestsQueryShape)(error);
          setRequests([]);
        });
      requestUnsubscribers.push(() => {
        isRequestsFetchCancelled = true;
      });
    }

    // time_entries (self)
    const timeEntryUserIds = getCurrentUserIdentityIds(currentUser);
    if (timeEntryUserIds.length === 0) {
      console.warn('time_entries query skipped: missing current user identity ids');
    }
    if (import.meta.env.DEV) console.debug('time_entries query IDs:', timeEntryUserIds);
    const timeEntriesQuery =
      timeEntryUserIds.length > 1
        ? query(collection(db, 'time_entries'), where('userId', 'in', timeEntryUserIds))
        : query(collection(db, 'time_entries'), where('userId', '==', (timeEntryUserIds[0] ?? '__no_match__')));
    const unsubTimeEntries = onSnapshot(
      timeEntriesQuery,
      (snap: QuerySnapshot<DocumentData>) => {
        if (import.meta.env.DEV) console.debug('time_entries snapshot size:', snap.size);
        setTimeEntries(snap.docs.map(d => ({ id: d.id, ...d.data() } as TimeEntry)));
      },
      onIndexOrPerms('time_entries', 'self')
    );

    // feedback (TOP-LEVEL per rules: /feedback/{feedbackId})
    // Non-admin listeners must stay unit-scoped; sort client-side to avoid composite index requirements.
    const feedbackUnitIds = profileScopedUnitIds.slice(0, 10);
    const feedbackCollection = collection(db, 'feedback');
    const feedbackQueryRef: Query<DocumentData> = feedbackUnitIds.length > 0
        ? query(feedbackCollection, where('unitId', 'in', feedbackUnitIds))
        : query(feedbackCollection, where(documentId(), '==', '__no_matching_feedback__'));

    logListenerScope('feedback', feedbackUnitIds);
    const unsubFeedback = onSnapshot(
      feedbackQueryRef,
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Feedback));
        if (!isSuperAdmin) {
          docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        }
        setFeedbackList(docs);
      },
      onIndexOrPerms('feedback', feedbackUnitIds)
    );

    // polls (TOP-LEVEL per rules: /polls/{pollId})
    // Non-admin listeners must stay unit-scoped; sort client-side to avoid composite index requirements.
    const pollUnitIds = profileScopedUnitIds.slice(0, 10);
    const pollsCollection = collection(db, 'polls');
    const pollsQueryRef: Query<DocumentData> = pollUnitIds.length > 0
        ? query(pollsCollection, where('unitId', 'in', pollUnitIds))
        : query(pollsCollection, where(documentId(), '==', '__no_matching_poll__'));

    logListenerScope('polls', pollUnitIds);
    const unsubPolls = onSnapshot(
      pollsQueryRef,
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Poll));
        if (!isSuperAdmin) {
          docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        }
        setPolls(docs);
      },
      onIndexOrPerms('polls', pollUnitIds)
    );

    return () => {
      unsubUnitPerms.forEach(u => u());
      unsubTodos();
      unsubAdminTodos();
      requestUnsubscribers.forEach(unsubscribe => unsubscribe());
      unsubTimeEntries();
      unsubFeedback();
      unsubPolls();
    };
  }, [
    appState,
    currentUser?.id,
    effectiveCurrentUser?.id,
    effectiveCurrentUser?.role,
    isAccessProfileLoaded,
    isAccessProfileBlocked,
    isDemoMode,
    authorizationUnitIdsKey,
    JSON.stringify(allUnits.map(unit => unit.id)),
    JSON.stringify(permissions),
    JSON.stringify(unitPermissions),
  ]);


  useEffect(() => {
    if (appState !== 'dashboard' || !currentUser) return;
    void warmBackend();
  }, [appState, currentUser?.id]);

  // ---------- Title ----------
  useEffect(() => {
    switch (appState) {
      case 'login':
        document.title = 'Sign in - mintleaf.hu';
        break;
      case 'register':
        document.title = 'Regisztráció - mintleaf.hu';
        break;
      case 'dashboard':
        document.title = 'Dashboard - mintleaf.hu';
        break;
      case 'auth-profile-error':
        document.title = 'Profil betöltési hiba - mintleaf.hu';
        break;
      default:
        document.title = 'MintLeaf';
    }
  }, [appState]);

  const handleLogout = async () => {
    if (isDemoMode) {
      window.location.href = window.location.pathname;
      return;
    }
    try {
      await signOut(auth);
      clearSessionOwnedState({ appState: getSignedOutAppState(), clearLegacyApiToken: true });
    } catch (err) {
      console.error('Logout failed', authErrorDetails(err));
    }
  };

  const retryProfileBootstrap = async () => {
    const firebaseUser = auth.currentUser;
    logAuthDiagnostic('profile-bootstrap:retry', {
      hasCurrentUser: Boolean(firebaseUser),
    });

    if (!firebaseUser) {
      const errorMessage = 'Profil betöltési hiba: nincs aktív Firebase munkamenet.';
      setAuthBootstrapError(errorMessage);
      setAuthBootstrapStatus('signed-out');
      setAppState('login');
      logAuthDiagnostic('profile-bootstrap:error', {
        reason: 'missing-current-user',
      });
      return;
    }

    const activeProfileBootstrapSequence = activeProfileBootstrapSequenceRef.current;
    if (activeProfileBootstrapSequence !== null) {
      logAuthDiagnostic('profile-bootstrap:skipped', {
        uid: firebaseUser.uid,
        source: 'manual-retry',
        reason: 'already-running',
        activeSequence: activeProfileBootstrapSequence,
      });
      return;
    }

    const pathname = window.location.pathname;
    setCurrentUser(null);
    setAccessProfile(null);
    setIsAccessProfileLoaded(false);
    setAppState('loading');
    await startProfileBootstrap(
      firebaseUser,
      'manual-retry',
      firebaseAuthUidRef.current,
      pathname.startsWith('/reserve'),
      pathname.startsWith('/manage')
    );
  };

  const handleProfileErrorLogout = async () => {
    const reason = 'profile-error-manual-logout';
    logAuthDiagnostic('forced-signout:start', { reason });
    try {
      await signOut(auth);
      logAuthDiagnostic('forced-signout:success', { reason });
      clearSessionOwnedState({ appState: 'login', clearLegacyApiToken: true });
    } catch (error) {
      logAuthDiagnostic('forced-signout:error', {
        reason,
        error: authErrorDetails(error),
      });
      console.error('Profile error manual logout failed', authErrorDetails(error));
    }
  };

  const handleRegisterSuccess = () => {
    window.location.href = window.location.pathname;
  };

  // ---------- Render ----------
  switch (appState) {
    case 'public':
      if (publicPage?.type === 'reserve') {
        return (
          <PublicRouteDiagnosticsBoundary routeType="reserve">
            <ReservationPage unitId={publicPage.unitId} />
          </PublicRouteDiagnosticsBoundary>
        );
      }
      if (publicPage?.type === 'manage') {
        return (
          <PublicRouteDiagnosticsBoundary routeType="manage">
            <ManageReservationPage
              unitId={publicPage.unitId}
              reservationId={publicPage.reservationId}
              manageToken={publicPage.manageToken}
            />
          </PublicRouteDiagnosticsBoundary>
        );
      }
      return <div className="fixed inset-0 flex items-center justify-center">Hiba: {publicPage?.message}</div>;

    case 'loading':
      return (
        <div className="fixed inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      );

    case 'register':
      return (
        <div className="fixed inset-0 flex items-center justify-center">
          <Register inviteCode={inviteCode!} onRegisterSuccess={handleRegisterSuccess} />
        </div>
      );

    case 'dashboard':
      if (!effectiveCurrentUser) {
        if (import.meta.env.DEV) {
          console.debug('[dashboard-render] loading dashboard shell', {
            hasCurrentUser: Boolean(currentUser),
            hasEffectiveCurrentUser: Boolean(effectiveCurrentUser),
            isAccessProfileLoaded,
            hasAccessProfile: Boolean(accessProfile),
            appState,
          });
        }
        return (
          <div className="fixed inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        );
      }
      if (!isDemoMode && currentUser && !isAccessProfileLoaded) {
        if (import.meta.env.DEV) {
          console.debug('[dashboard-render] loading dashboard shell', {
            hasCurrentUser: Boolean(currentUser),
            hasEffectiveCurrentUser: Boolean(effectiveCurrentUser),
            isAccessProfileLoaded,
            hasAccessProfile: Boolean(accessProfile),
            appState,
          });
        }
        return (
          <div className="fixed inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        );
      }
      if (import.meta.env.DEV) {
        console.debug('[dashboard-render] rendering dashboard', {
          hasCurrentUser: Boolean(currentUser),
          hasEffectiveCurrentUser: Boolean(effectiveCurrentUser),
          isAccessProfileLoaded,
          hasAccessProfile: Boolean(accessProfile),
          appState,
        });
      }
      return (
        <UnitProvider currentUser={effectiveCurrentUser} allUnits={allUnits}>
          <ThemeManagerBridge
            allUnits={allUnits}
            bases={themeBases}
            previewBases={previewBases}
            themeMode={themeMode}
            useBrandTheme={useBrandTheme}
          />
          <DashboardWithUnitGate currentUser={effectiveCurrentUser} allUnits={allUnits}>
            <React.Suspense
              fallback={(
                <div className="fixed inset-0 flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              )}
            >
              <Dashboard
              currentUser={effectiveCurrentUser}
              onLogout={handleLogout}
              isDemoMode={isDemoMode}
              requests={requests}
              shifts={shifts}
              isShiftsLoading={isShiftsLoading}
              isShiftsRefreshing={isShiftsRefreshing}
              hasLoadedShiftsOnce={hasLoadedShiftsOnce}
              hasUsableCurrentScopeSchedule={hasUsableCurrentScopeSchedule}
              schedulerRefreshWarning={schedulerRefreshWarning}
              dashboardTodayShiftStatus={dashboardTodayShiftStatus}
              todos={todos}
              adminTodos={adminTodos}
              allUnits={allUnits}
              allUsers={allUsers}
              permissions={permissions}
              unitPermissions={unitPermissions}
              timeEntries={timeEntries}
              feedbackList={feedbackList}
              polls={polls}
              firestoreError={firestoreError}
              themeMode={themeMode}
              onThemeModeChange={setThemeMode}
              themeBases={themeBases}
              onThemeBasesChange={setPreviewBases}
              useBrandTheme={useBrandTheme}
              onBrandChange={setUseBrandTheme}
              onSchedulerRefresh={refreshSchedulerShifts}
              onSchedulerFirstPaintComplete={markSchedulerFirstPaintComplete}
              onActiveAppChange={handleDashboardActiveAppChange}
              />
            </React.Suspense>
          </DashboardWithUnitGate>
        </UnitProvider>
      );

    case 'auth-profile-error':
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-100 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 text-center shadow-xl">
            <h1 className="mb-3 text-2xl font-bold text-slate-900">Profil betöltési hiba</h1>
            <p className="mb-4 text-sm text-slate-700">
              A Firebase bejelentkezés sikerült, de a Mintleaf profil betöltése nem fejeződött be.
            </p>
            <p className="mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {authBootstrapError || 'Profil betöltési hiba.'}
            </p>
            {shouldShowAuthBootstrapDiagnostic && (
              <p className="mb-6 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-600">
                Auth diagnosztika státusz: {authBootstrapStatus}
              </p>
            )}
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={retryProfileBootstrap}
                className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Újrapróbálom
              </button>
              <button
                type="button"
                onClick={handleProfileErrorLogout}
                className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Kijelentkezés
              </button>
            </div>
          </div>
        </div>
      );

    case 'login':
    default:
      return (
        <div className="fixed inset-0">
          {shouldShowAuthBootstrapDiagnostic && authBootstrapError && (
            <div className="absolute left-1/2 top-4 z-50 w-[min(92vw,42rem)] -translate-x-1/2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow">
              Auth diagnosztika: {authBootstrapError}
            </div>
          )}
          <Login loginMessage={loginMessage} />
        </div>
      );
  }
};

export default App;
